import {fromBase64url} from "core/crypto/keys";
import type {User} from "account/User";
import {majorToMinor, minorToMajor} from "core/format/minorUnits";
import {nextUpgradePlan, planOf, type Plan} from "home/gating";
import {BAKED_PRICES} from "pricing/bakedCatalogue";
import {PAID_PLAN_IDS, planIdForCode, type MonthlyCatalogue, type PaidPlanId} from "pricing/planCatalogue";
import {planEntitlements} from "pricing/planEntitlements";
import type {ExitOfferVariant} from "upsell/exitOffer";
import {firstMonthMajor} from "upsell/firstMonthPricing";
import {parseLadderToken} from "./ladderOffer";

/**
 * Field types come only from the generated API; the display prerequisites are required locally. `code` and
 * `discountsLeftToday` are a COUPON's; the Halloween ticket (kitecorp/transferify-api-service#3013,
 * kitecorp/transferify-ui#3776) carries neither and answers `kind: INTRO` with the first `introDays` of the plan
 * for `introMinor`, then `renewalMinor` a month, both in the response's own currency and minor units, under the
 * `offerVariant` the checkout sends back beside the token.
 */
export type CurrentLadderOffer = Required<Pick<import("api/schemaAssert").Schemas["CurrentLadderOfferResponse"],
	"presentationId" | "offerKey" | "targetPlan" | "expiresAt" | "arm">>
	& Pick<import("api/schemaAssert").Schemas["CurrentLadderOfferResponse"],
		"code" | "discountsLeftToday" | "percentOff" | "amountOff" | "currency" | "checkoutPath" | "offerToken"
		| "kind" | "introDays" | "introMinor" | "renewalMinor" | "offerVariant">;

/** The one success-card offer sold as an intro line item rather than a coupon: the Halloween ticket. */
export type TicketVariant = Extract<ExitOfferVariant, "halloween30">;
export type CurrentLadderResolution = {status: "offer"; offer: CurrentLadderOffer} | {status: "none"} | {status: "unavailable"};
export interface CurrentLadderAnswer {accountKey: string; resolution: CurrentLadderResolution}
export const CURRENT_LADDER_REFERENCE_PREFIX = "current.";

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** Existing account-owned email references retain their strict UUID parsing. */
export function currentLadderSendId(reference: string): string | null {
	const id = reference.slice(CURRENT_LADDER_REFERENCE_PREFIX.length);
	return reference.startsWith(CURRENT_LADDER_REFERENCE_PREFIX) && UUID_PATTERN.test(id) ? id : null;
}

/** Upload/download routes use encoded capabilities; coupon issuance alone requires the underlying UUID. */
export function couponTransferId(encoded: string | null): string | null {
	if (encoded === null) return null;
	if (UUID_PATTERN.test(encoded)) return encoded;
	try {
		const id = new TextDecoder().decode(fromBase64url(encoded));
		return UUID_PATTERN.test(id) ? id : null;
	} catch {
		return null;
	}
}

/**
 * Wait for the storefront and refuse while the higher-priority campaign HOLDS the slot; coupon
 * frequency belongs to the API.
 *
 * A campaign request merely in flight is a separate rule (`couponAdmissionKey` in `./couponCountdown`,
 * ui#2486) because the answer depends on what is already on screen, which this function cannot see.
 */
export function currentLadderAccountKey(signedIn: boolean, user: User,
	admission?: {hostname: string; campaignActive: boolean}): string | null {
	if (admission !== undefined && (admission.hostname === "" || admission.campaignActive)) return null;
	return signedIn && user.subscriptionPeriod !== "YEARLY" && nextUpgradePlan(planOf(user)) !== null
		? user.accountId ?? user.id ?? user.email : null;
}

export type CurrentCouponDiscount = {kind: "percent"; percentOff: number} | {kind: "amount"; amountOff: number};
/** Display terms shared by coupon entries; no cadence or checkout authorization belongs here. */
export interface CouponGift {
	targetPlan: PaidPlanId;
	currency: string;
	discount: CurrentCouponDiscount;
	firstMonth: number;
	renewal: number;
	maxHostedUploadBytes: number | null;
	isUpgrade: boolean;
	/**
	 * Set on the Halloween ticket (kitecorp/transferify-ui#3776): `firstMonth` buys the first `days` of the
	 * plan as an intro line item under `variant`, which the checkout sends beside the ladder token so the api
	 * shapes the Session as a ticket. Absent on a coupon, whose `firstMonth` is one discounted invoice.
	 */
	intro?: {days: number; variant: TicketVariant};
}

/**
 * The same plan at its REGULAR price, with no coupon: what the limit-wall email's link shows once the
 * day's redemptions are spent (owner decision C7, kitecorp/transferify-api-service#2206). `discount: null`
 * is the discriminant, and there is no first-month figure, so no view can state a reduction it lacks.
 */
export interface RegularPriceQuote extends Omit<CouponGift, "discount" | "firstMonth" | "intro"> { discount: null }

/** What the claim dialog can state: a coupon's two prices, or one regular price. */
export type ClaimQuote = CouponGift | RegularPriceQuote;

export interface CurrentLadderGift extends CouponGift { offer: CurrentLadderOffer }

/** A paid subscription keeps its billing currency when the viewer changes locale or country. */
export function currentLadderCurrency(offer: CurrentLadderOffer | null, storefrontCurrency: string): string {
	return offer?.currency ?? storefrontCurrency;
}

export function currentCouponToken(offer: CurrentLadderOffer): string | null {
	if (offer.targetPlan !== "BASIC") return typeof offer.offerToken === "string" && offer.offerToken.length > 0 && offer.offerToken.length <= 64 ? offer.offerToken : null;
	if (typeof offer.checkoutPath !== "string" || !offer.checkoutPath.startsWith("/pricing?")) return null;
	return parseLadderToken(offer.checkoutPath.slice("/pricing".length));
}

function couponDestinationValid(offer: CurrentLadderOffer): boolean {
	return currentCouponToken(offer) !== null;
}

function isTicket(offer: CurrentLadderOffer): boolean {
	return offer.kind === "INTRO";
}

/** A coupon needs a redemption left today; the ticket has no daily count, so the api's answer alone admits it. */
function inStock(offer: CurrentLadderOffer): boolean {
	return isTicket(offer) || isPositiveInteger(offer.discountsLeftToday);
}

function isPositiveInteger(value: number | null | undefined): value is number {
	return typeof value === "number" && Number.isInteger(value) && value > 0;
}

/** The count a coupon's print and dialog show; the ticket shows none, having none. */
export function couponDiscountsLeft(gift: CurrentLadderGift): number | undefined {
	return gift.intro === undefined && isPositiveInteger(gift.offer.discountsLeftToday) ? gift.offer.discountsLeftToday : undefined;
}

type GiftTerms = Pick<CouponGift, "discount" | "firstMonth" | "renewal" | "intro">;

/**
 * The sender's next plan on the terms the api answered: a coupon's one discounted invoice, or the ticket's
 * intro period. Either way the plan must be the next one up, and the checkout the answer names must be live.
 */
export function currentLadderGift(offer: CurrentLadderOffer | null, currency: string, currentPlan: Plan, prices: MonthlyCatalogue = BAKED_PRICES): CurrentLadderGift | null {
	if (offer === null || !inStock(offer) || !couponDestinationValid(offer)) return null;
	const targetPlan = PAID_PLAN_IDS.find(plan => plan === planIdForCode(offer.targetPlan));
	if (targetPlan === undefined || targetPlan !== nextUpgradePlan(currentPlan)) return null;
	const terms = isTicket(offer) ? ticketTerms(offer, currency) : couponTerms(offer, currency, targetPlan, prices);
	if (terms === null) return null;
	return {offer, targetPlan, currency, ...terms,
		maxHostedUploadBytes: planEntitlements(targetPlan).maxHostedUploadBytes, isUpgrade: currentPlan !== "free"};
}

/**
 * Quote the next plan from the baked catalogue in the buyer's currency; never convert an amount-off. The
 * success card hands in the live quote for its own currency and keeps the snapshot for any other.
 */
function couponTerms(offer: CurrentLadderOffer, currency: string, targetPlan: PaidPlanId, prices: MonthlyCatalogue): GiftTerms | null {
	const baked = prices[currency];
	if (baked === undefined || (offer.currency != null && offer.currency !== currency)) return null;
	const renewal = baked[targetPlan].monthly;
	if (!Number.isFinite(renewal) || renewal <= 0) return null;
	let discount: CurrentCouponDiscount;
	let firstMonth: number | null;
	if (offer.percentOff != null && offer.amountOff == null) {
		discount = {kind: "percent", percentOff: offer.percentOff};
		firstMonth = firstMonthMajor(renewal, currency, offer.percentOff);
	} else if (offer.amountOff != null && offer.percentOff == null && offer.currency === currency
		&& Number.isInteger(offer.amountOff) && offer.amountOff > 0) {
		discount = {kind: "amount", amountOff: minorToMajor(offer.amountOff, currency)};
		firstMonth = minorToMajor(Math.max(0, majorToMinor(renewal, currency) - offer.amountOff), currency);
	} else return null;
	if (firstMonth === null) return null;
	return {discount, firstMonth, renewal};
}

/**
 * The Halloween ticket (kitecorp/transferify-ui#3776): every figure is the api's, never the catalogue's. The
 * api prices the intro and the renewal from the Stripe Prices the Session will carry, so a catalogue quote
 * here could state a renewal the Session does not charge. A ticket in another currency than the storefront's
 * is no ticket, as a subscription keeps its currency; one missing a figure, or priced at or above its own
 * renewal, is refused rather than shown with a reduction it cannot state.
 */
function ticketTerms(offer: CurrentLadderOffer, currency: string): GiftTerms | null {
	const {introDays, introMinor, renewalMinor, offerVariant} = offer;
	if (offerVariant !== "halloween30" || offer.currency !== currency || !isPositiveInteger(introDays)
		|| !isPositiveInteger(introMinor) || !isPositiveInteger(renewalMinor) || introMinor >= renewalMinor) return null;
	return {
		firstMonth: minorToMajor(introMinor, currency), renewal: minorToMajor(renewalMinor, currency),
		discount: {kind: "amount", amountOff: minorToMajor(renewalMinor - introMinor, currency)},
		intro: {days: introDays, variant: offerVariant},
	};
}

/** Admission can change before effects run; no answer for this account means it is still pending. */
export function currentLadderOfferLoading(accountKey: string | null, answer: CurrentLadderAnswer | null): boolean {
	return accountKey !== null && answer?.accountKey !== accountKey;
}

/** Expired/sold-out coupons and another account's response never enter the slot. */
export function visibleCurrentLadderOffer(accountKey: string | null, answer: CurrentLadderAnswer | null, now: number): CurrentLadderOffer | null {
	if (accountKey === null || answer?.accountKey !== accountKey || answer.resolution.status !== "offer") return null;
	const offer = answer.resolution.offer;
	return inStock(offer) && Date.parse(offer.expiresAt) > now && couponDestinationValid(offer) ? offer : null;
}
